import React, { Children } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import {
    Button, ButtonGroup, Progress,
    UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faStepBackward, faStepForward } from '@fortawesome/free-solid-svg-icons';
import { Hide } from '@s20/components/common/Hide';
import { convertWinToIANA } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes } from '../Common';
import ValidatedSelect, { CompactSelectStyles } from '../common/forms/ValidatedSelect';
import Input from '../common/forms/Input';
import CalendarJobDetails from './CalendarJobDetails';
import CalendarEvent from './CalendarEvent';
import CalendarDateHeaderWithPopover from './CalendarDateHeaderWithPopover';
import WeekViewEvent from './WeekViewEvent';
import WeekViewHeaderOverlay from './WeekViewHeaderOverlay';
import { transformJobsToEvents } from './calendarEvents';
import './CalendarJobs.scss';
import 'moment-timezone';

const localizer = momentLocalizer(moment);

const navigate = {
    PREVIOUS: 'PREV',
    NEXT: 'NEXT',
    TODAY: 'TODAY',
    DATE: 'DATE',
};

const VIEW = {
    MONTH: 'month',
    WEEK: 'week',
    DAY: 'day',
};

const views = [
    VIEW.MONTH,
];

// Job status options for the Select dropdown
const jobStatusOptions = [
    { value: null, label: 'All Statuses' },
    { value: 'Unfilled', label: 'Unfilled' },
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'No Response', label: 'No Response' },
    { value: 'Confirmed', label: 'Confirmed' },
    { value: 'Cancel Pending', label: 'Cancel Pending' },
    { value: 'Cancel Confirmed', label: 'Cancel Confirmed' },
];

const groupEventsByTimeSlot = (events) => {
    // Group events by date and 30-minute time slot
    const groupedEvents = {};

    events.forEach((event) => {
        const eventMoment = moment(event.start);
        const dateKey = eventMoment.format('YYYY-MM-DD');

        // Round down to nearest 30 minutes for grouping
        const minutes = eventMoment.minutes();
        const roundedMinutes = Math.floor(minutes / 30) * 30;
        eventMoment.minutes(roundedMinutes);
        eventMoment.seconds(0);

        const timeSlotKey = `${dateKey}_${eventMoment.format('HH:mm')}`;

        if (!groupedEvents[timeSlotKey]) {
            groupedEvents[timeSlotKey] = {
                start: eventMoment.toDate(),
                end: eventMoment.clone().add(30, 'minutes').toDate(),
                events: [],
            };
        }

        groupedEvents[timeSlotKey].events.push(event);
    });

    // Convert grouped events to array with aggregated data
    const resultEvents = [];

    Object.keys(groupedEvents).forEach((key) => {
        const group = groupedEvents[key];

        // If only one event in this time slot, return it as-is
        if (group.events.length === 1) {
            resultEvents.push(group.events[0]);
        } else {
            // Multiple events - create grouped header
            const totalStaff = group.events.reduce((sum, e) => sum + (e.resource?.staffCount || 0), 0);
            const totalVehicles = group.events.reduce((sum, e) => sum + (e.resource?.vehicleCount || 0), 0);

            resultEvents.push({
                id: `group_${key}`,
                title: `${group.events.length} jobs`,
                start: group.start,
                end: group.end,
                resource: {
                    isGrouped: true,
                    groupedEvents: group.events,
                    jobCount: group.events.length,
                    totalStaff,
                    totalVehicles,
                    timeSlot: moment(group.start).format('h:mm A'),
                },
            });
        }
    });

    return resultEvents;
};

const getDateRange = (date, view) => {
    const momentDate = moment(date);

    switch (view) {
        case VIEW.MONTH:
            return {
                startDate: momentDate.clone().startOf('month').startOf('week'),
                endDate: momentDate.clone().endOf('month').endOf('week'),
            };
        case VIEW.WEEK:
            return {
                startDate: momentDate.clone().startOf('week'),
                endDate: momentDate.clone().endOf('week'),
            };
        case VIEW.DAY:
            return {
                startDate: momentDate.clone().startOf('day'),
                endDate: momentDate.clone().endOf('day'),
            };
        default:
            return {
                startDate: momentDate.clone().startOf('month'),
                endDate: momentDate.clone().endOf('month'),
            };
    }
};

// Custom Date Cell Wrapper for Month View - matches AssignmentCalendar approach
const CalendarDateCellWrapper = ({
    children, value,
}) => {
    const today = moment();
    const cellDate = moment(value);

    // Set background color class like AssignmentCalendar
    let className = 'calendar-cell--future'; // Future days - very light grey
    if (today.isAfter(cellDate, 'day')) {
        className = 'calendar-cell--past'; // Past days - light grey
    } else if (today.isSame(cellDate, 'day')) {
        className = 'calendar-cell--today'; // Today - light blue
    }

    return React.cloneElement(Children.only(children), {
        className: `${children.props.className || ''} ${className}`.trim(),
    });
};

class CalendarJobs extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.vm = new BaseFormViewModel();

        this.state = {
            events: [],
            allJobs: [], // Store all jobs for client-side filtering
            loading: false, // Start with false so calendar renders immediately
            error: null,
            selectedJob: null,
            showDetails: false,
            currentDate: new Date(),
            currentView: VIEW.MONTH,
            filters: {
                dispatchLocationId: null,
                searchText: '',
                contractId: null,
                jobTypeId: null,
                jobStatus: null,
            },
            dispatchLocations: [],
            contracts: [],
            jobTypes: [],
            selectedDispatchTimezone: null,
            // Memoized filter operations for performance
            memoizedFilters: {
                lastAllJobs: null,
                lastFilters: null,
                lastCurrentView: null,
                lastFilteredJobs: null,
                lastEvents: null,
            },
            // Memoized moment instances for performance
            memoizedMoments: {
                today: null,
                todayDate: null,
                currentMonth: null,
                currentMonthDate: null,
            },
        };
    }

    async componentDidMount() {
        // Initialize memoized moments
        this.updateMemoizedMoments();
        await this.populateState();
    }

    componentDidUpdate() {
        // Update memoized moments on each update to catch day changes
        this.updateMemoizedMoments();
    }

    updateMemoizedMoments = () => {
        const now = new Date();
        const today = now.getDate();
        const month = now.getMonth();
        const year = now.getFullYear();

        // Only update if day or month has changed
        if (!this.state.memoizedMoments.today ||
            this.state.memoizedMoments.todayDate !== today ||
            this.state.memoizedMoments.currentMonth !== month) {
            this.setState({
                memoizedMoments: {
                    today: moment(),
                    todayDate: today,
                    currentMonth: month,
                    currentMonthDate: `${year}-${month}`,
                },
            });
        }
    };

    populateState = async () => {
        try {
            // Load dispatch locations for filter
            const dispatchLocations = await httpClient.js(ApiRoutes.typeAheads.dispatchLocations());

            // Default to first dispatch location if available
            const defaultLocationId = dispatchLocations.length > 0 ? dispatchLocations[0].value : null;

            const defaultLocation = dispatchLocations.find((loc) => loc.value === defaultLocationId);

            const selectedTimezone = defaultLocation?.timeZone || null;
            this.setState((prevState) => ({
                dispatchLocations,
                filters: {
                    ...prevState.filters,
                    dispatchLocationId: defaultLocationId,
                },
                selectedDispatchTimezone: selectedTimezone,
            }), () => {
                // Load calendar jobs after dispatch locations are set
                if (dispatchLocations.length > 0) {
                    this.loadCalendarJobs();
                }
            });
        } catch {
            this.setState({ error: 'Failed to load dispatch locations' });
        }
    };

    loadCalendarJobs = async () => {
        try {
            this.setState({ loading: true });

            const { currentDate, currentView, filters } = this.state;

            // Calculate date range based on current view
            const { startDate, endDate } = getDateRange(currentDate, currentView);

            // A dispatch location is required now
            if (!filters.dispatchLocationId) {
                // No location selected, clear jobs
                this.setState({
                    allJobs: [], events: [], loading: false, error: null,
                });
                return;
            }

            // Build request body for POST
            const requestBody = {
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                includeDeleted: false,
                dispatchLocationId: filters.dispatchLocationId,
                contractId: filters.contractId || null,
                jobTypeId: filters.jobTypeId || null,
            };

            const jobs = await httpClient.post(ApiRoutes.calendarJobs, requestBody);

            // Store all jobs and apply client-side filtering
            this.setState({ allJobs: jobs, loading: false, error: null }, () => {
                this.applyClientSideFilters();
            });
        } catch (error) {
            this.setState({ error: error.message, loading: false });
        }
    };

    applyClientSideFilters = () => {
        const {
            allJobs, filters, currentView, memoizedFilters,
        } = this.state;

        // Memoization check - skip expensive filtering if inputs haven't changed
        const filtersChanged = JSON.stringify(filters) !== JSON.stringify(memoizedFilters.lastFilters);
        const jobsChanged = allJobs !== memoizedFilters.lastAllJobs;
        const viewChanged = currentView !== memoizedFilters.lastCurrentView;

        if (!filtersChanged && !jobsChanged && !viewChanged && memoizedFilters.lastEvents) {
            // Use cached result
            this.setState({ events: memoizedFilters.lastEvents });
            return;
        }

        let filteredJobs = [...allJobs];

        // Apply search filter client-side
        if (filters.searchText && filters.searchText.trim()) {
            const searchTerm = filters.searchText.toLowerCase();
            filteredJobs = filteredJobs.filter((job) =>
                (job.jobNumber && job.jobNumber.toLowerCase().includes(searchTerm)) ||
                (job.description && job.description.toLowerCase().includes(searchTerm)) ||
                (job.customerName && job.customerName.toLowerCase().includes(searchTerm)) ||
                (job.workOrderNumber && job.workOrderNumber.toLowerCase().includes(searchTerm)),
            );
        }

        // Apply job status filter client-side
        if (filters.jobStatus) {
            filteredJobs = filteredJobs.filter((job) => {
                const jobStatus = job.jobStatus || job.status || 'Unfilled';
                return jobStatus === filters.jobStatus;
            });
        }

        let events = transformJobsToEvents(filteredJobs);

        // For Week View, adjust duration and group events by 30-minute time slots
        if (currentView === VIEW.WEEK) {
            events = groupEventsByTimeSlot(events);
        }

        // Cache the results
        this.setState({
            memoizedFilters: {
                lastAllJobs: allJobs,
                lastFilters: { ...filters },
                lastCurrentView: currentView,
                lastFilteredJobs: filteredJobs,
                lastEvents: events,
            },
            events,
        });
    };

    handleNavigate = (action, newDate) => {
        let date = newDate || this.state.currentDate;

        if (typeof action === 'string') {
            const momentDate = moment(this.state.currentDate);

            switch (action) {
                case navigate.PREVIOUS: {
                    let unit = VIEW.MONTH;
                    if (this.state.currentView === VIEW.DAY) unit = VIEW.DAY;
                    else if (this.state.currentView === VIEW.WEEK) unit = VIEW.WEEK;
                    date = momentDate.subtract(1, unit).toDate();
                    break;
                }
                case navigate.NEXT: {
                    let unit = VIEW.MONTH;
                    if (this.state.currentView === VIEW.DAY) unit = VIEW.DAY;
                    else if (this.state.currentView === VIEW.WEEK) unit = VIEW.WEEK;
                    date = momentDate.add(1, unit).toDate();
                    break;
                }
                case navigate.TODAY:
                    date = new Date();
                    break;
                default:
                    break;
            }
        }

        this.setState({ currentDate: date }, () => {
            this.loadCalendarJobs();
        });
    };

    handleViewChange = (view) => {
        const { currentView } = this.state;

        // Define view hierarchy: day < week < month
        const viewHierarchy = { [VIEW.DAY]: 0, [VIEW.WEEK]: 1, [VIEW.MONTH]: 2 };

        // Check if we're changing to a bigger view (more data needed)
        const shouldReload = viewHierarchy[view] > viewHierarchy[currentView];

        this.setState({ currentView: view }, () => {
            if (shouldReload) {
                // Force reload data when switching to bigger view
                this.loadCalendarJobs();
            } else {
                // Just reapply filters for same/smaller view
                this.applyClientSideFilters();
            }
        });
    };

    handleSelectEvent = (event) => {
        // Enhance the job with resolved dispatch location name
        const job = { ...event.resource };

        if (job.dispatchLocationId) {
            const dispatchLocation = this.state.dispatchLocations.find((loc) => loc.value === job.dispatchLocationId);
            if (dispatchLocation) {
                job.dispatchLocationName = dispatchLocation.label;
            } else {
                job.dispatchLocationName = `Location ID: ${job.dispatchLocationId}`;
            }
        } else {
            job.dispatchLocationName = 'No Dispatch Location';
        }

        this.setState({
            selectedJob: job,
            showDetails: true,
        });
    };

    handleCloseDetails = () => {
        this.setState({
            selectedJob: null,
            showDetails: false,
        });
    };

    renderCalendarTitle = () => {
        const { currentDate, selectedDispatchTimezone } = this.state;
        const dateStr = moment(currentDate).format('MMMM YYYY');

        if (!selectedDispatchTimezone) {
            return dateStr;
        }

        // Convert Windows timezone to IANA format for moment-timezone
        const ianaTimezone = convertWinToIANA(selectedDispatchTimezone);
        if (!ianaTimezone) {
            return dateStr;
        }

        const browserTimezone = moment.tz.guess();

        if (ianaTimezone !== browserTimezone) {
            const timezoneAbbr = moment.tz(currentDate, ianaTimezone).format('z');
            return `${dateStr} (${timezoneAbbr})`;
        }

        return dateStr;
    };

    handleFilterChange = (filterName, value) => {
        let selectedTimezone = null;

        if (filterName === 'dispatchLocationId') {
            const numericValue = value ? Number(value) : null;
            const selectedLocation = this.state.dispatchLocations.find((loc) => loc.value === numericValue);
            selectedTimezone = selectedLocation?.timeZone || null;
        }

        this.setState((prevState) => {
            const newState = {
                filters: {
                    ...prevState.filters,
                    [filterName]: value,
                },
            };

            if (filterName === 'dispatchLocationId') {
                newState.selectedDispatchTimezone = selectedTimezone;
            }

            return newState;
        }, () => {
            // For search text, apply filters immediately client-side
            if (filterName === 'searchText') {
                this.applyClientSideFilters();
            } else {
                // For other filters, reload from server
                this.loadCalendarJobs();
            }
        });
    };

    // Handler for react-select onChange (receives option object instead of raw value)
    handleSelectChange = (filterName, selectedOption) => {
        const value = selectedOption ? selectedOption.value : null;
        this.handleFilterChange(filterName, value);
    };

    render() {
        const {
            events, loading, error, selectedJob, showDetails, filters, dispatchLocations,
        } = this.state;

        if (error) {
            return <div className="alert alert-danger">Error: {error}</div>;
        }

        return (
            <div className="calendar-jobs calendar-jobs__container d-flex flex-column">
                {/* Loading Indicator */}
                {loading && <Progress />}

                {/* Header with taller buttons matching AssignmentCalendar */}
                <div className="calendar-header flex-shrink-0 d-flex justify-content-between align-items-center border-bottom">
                    <div className="d-flex align-items-center">
                        <ButtonGroup className="mr-3">
                            <Button
                                color="outline-primary"
                                className="p-1"
                                onClick={() => this.handleNavigate(navigate.PREVIOUS)}
                            >
                                <FontAwesomeIcon icon={faStepBackward} />
                            </Button>
                            <Button
                                color="outline-primary"
                                className="p-1"
                                onClick={() => this.handleNavigate(navigate.TODAY)}
                            >
                                <small>TODAY</small>
                            </Button>
                            <Button
                                color="outline-primary"
                                className="p-1"
                                onClick={() => this.handleNavigate(navigate.NEXT)}
                            >
                                <FontAwesomeIcon icon={faStepForward} />
                            </Button>
                        </ButtonGroup>

                        <h5 className="mb-0 text-center calendar-header__title">
                            {this.renderCalendarTitle()}
                        </h5>
                    </div>

                    <div className="d-flex align-items-center justify-content-end flex-grow-1">

                        {/* Desktop Filters - Inline */}
                        <Hide mdUp>
                            {/* Dispatch Location Filter */}
                            <ValidatedSelect
                                styles={CompactSelectStyles}
                                value={dispatchLocations.find((loc) => loc.value === filters.dispatchLocationId)}
                                onChange={(selectedOption) => this.handleSelectChange('dispatchLocationId', selectedOption)}
                                options={dispatchLocations}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                className="calendar-filter__dispatch-location mr-2"
                                classNamePrefix="react-select"
                            />

                            {/* Job Status Filter */}
                            <ValidatedSelect
                                styles={CompactSelectStyles}
                                value={jobStatusOptions.find((opt) => opt.value === filters.jobStatus)}
                                onChange={(selectedOption) => this.handleSelectChange('jobStatus', selectedOption)}
                                options={jobStatusOptions}
                                isClearable
                                className="calendar-filter__job-status mr-2"
                                classNamePrefix="react-select"
                            />

                            {/* Search Filter */}
                            <Input
                                type="text"
                                placeholder="Search jobs..."
                                value={filters.searchText}
                                onChange={(e) => this.handleFilterChange('searchText', e.target.value)}
                                className="calendar-filter__search"
                                formGroupClassname="mb-0"
                            />
                        </Hide>

                        {/* Mobile/Tablet Filters - Dropdown */}
                        <Hide smDown>
                            <UncontrolledDropdown className="mr-3">
                                <DropdownToggle
                                    color="outline-secondary"
                                    size="sm"
                                >
                                    <FontAwesomeIcon
                                        icon={faFilter}
                                        className="mr-1"
                                    />
                                    Filters
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem header>Search</DropdownItem>
                                    <div className="px-3 py-1">
                                        <Input
                                            type="text"
                                            size="sm"
                                            placeholder="Search jobs..."
                                            value={filters.searchText}
                                            onChange={(e) => this.handleFilterChange('searchText', e.target.value)}
                                        />
                                    </div>
                                    <DropdownItem divider />
                                    <DropdownItem header>Office</DropdownItem>
                                    <div className="px-3 py-1">
                                        <ValidatedSelect
                                            styles={CompactSelectStyles}
                                            value={dispatchLocations.find((loc) => loc.value === filters.dispatchLocationId)}
                                            onChange={(selectedOption) => this.handleSelectChange('dispatchLocationId', selectedOption)}
                                            options={dispatchLocations}
                                            getOptionLabel={(option) => option.label}
                                            getOptionValue={(option) => option.value}
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                    <DropdownItem divider />
                                    <DropdownItem header>Status</DropdownItem>
                                    <div className="px-3 py-1">
                                        <ValidatedSelect
                                            styles={CompactSelectStyles}
                                            value={jobStatusOptions.find((opt) => opt.value === filters.jobStatus)}
                                            onChange={(selectedOption) => this.handleSelectChange('jobStatus', selectedOption)}
                                            options={jobStatusOptions}
                                            isClearable
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </Hide>

                    </div>
                </div>

                {/* Main Calendar Area */}
                <div className="flex-grow-1 overflow-hidden bg-white position-relative">
                    <div className="h-100">
                        {/* Custom header overlay for Week View */}
                        {this.state.currentView === VIEW.WEEK && (
                            <WeekViewHeaderOverlay
                                events={events}
                                currentDate={this.state.currentDate}
                            />
                        )}
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            views={views}
                            view={this.state.currentView}
                            date={this.state.currentDate}
                            onView={this.handleViewChange}
                            onNavigate={this.handleNavigate}
                            onSelectEvent={this.handleSelectEvent}
                            slotPropGetter={(date) => {
                                const now = this.state.memoizedMoments.today;
                                const slotDate = moment(date);
                                let className = '';

                                if (slotDate.isSame(now, 'day')) {
                                    className = 'rbc-today week-time-slot--today';
                                } else if (slotDate.isBefore(now, 'day')) {
                                    className = 'rbc-past week-time-slot--past';
                                } else {
                                    className = 'rbc-future week-time-slot--future';
                                }

                                return { className };
                            }}
                            eventPropGetter={() =>
                            // Override all event styling to prevent indenting
                            ({
                                className: 'week-event',
                            })
                            }
                            formats={{
                                eventTimeRangeFormat: () => null,
                                timeGutterFormat: 'h:mm A',
                                dayFormat: 'DD ddd',
                                dayRangeHeaderFormat: ({ start, end }) =>
                                    `${moment(start).format('MMMM DD')} - ${moment(end).format('DD, YYYY')}`,
                            }}
                            components={{
                                toolbar: () => null, // Remove default toolbar
                                event: this.state.currentView === VIEW.WEEK ? WeekViewEvent : CalendarEvent,
                                month: {
                                    event: CalendarEvent, // Custom job card component for month view
                                    dateCellWrapper: CalendarDateCellWrapper, // Apply background colors to day cells in month view only
                                    dateHeader: (props) => (
                                        <CalendarDateHeaderWithPopover
                                            {...props}
                                            events={events.filter((event) =>
                                                moment(event.start).isSame(moment(props.date), 'day'),
                                            )}
                                            onJobClick={this.handleSelectEvent}
                                        />
                                    ),
                                },
                            }}
                            popup={false}
                            showMultiDayTimes
                            step={60}
                            timeslots={1}
                            className="rbc-calendar__full-height"
                        />
                    </div>
                </div>

                {showDetails && selectedJob && (
                    <CalendarJobDetails
                        job={selectedJob}
                        isOpen={showDetails}
                        onClose={this.handleCloseDetails}
                    />
                )}
            </div>
        );
    }
}

export default CalendarJobs;